// Regulatory + organizational signals from Client Intelligence enrichment.
// Translates Claude-extracted flags (publiclyTraded, processesPayments,
// handlesHealthData, governmentContractor, operatesInternationally) into
// universal threat profile weight adjustments.
//
// These flags are also collected via the assessment business-scoping step,
// but enrichment provides them earlier and without requiring assessment
// completion — improving the picture for advisor-only review.

// Returns { adjustments, evidence: [...] } where evidence is a human-readable
// list of reasons the advisor can show in the report.
export function detectRegulatorySignals(enrichment, businessScoping = {}) {
  const adj = { targetAssets: {}, attackVectors: {}, threatActors: {} }
  const evidence = []
  const reg = enrichment?.regulatoryProfile || {}
  const bs = businessScoping || {}

  // Cross-reference enrichment with assessment-side answers; either source counts
  const isPublic = !!(reg.publiclyTraded || asBoolean(bs['bs-6']))
  const processesPayments = !!(reg.processesPayments || bs['bs-8'])
  const handlesHealthData = !!(reg.handlesHealthData)
  const isGovContractor = !!(reg.governmentContractor || asBoolean(bs['bs-7']))
  const operatesInternationally = !!(reg.operatesInternationally)

  if (isPublic) {
    adj.threatActors.nation_state = (adj.threatActors.nation_state || 0) + 0.10
    adj.threatActors.lapsus_style_extortion = (adj.threatActors.lapsus_style_extortion || 0) + 0.10
    adj.targetAssets.material_nonpublic_information = (adj.targetAssets.material_nonpublic_information || 0) + 0.20
    evidence.push('Publicly traded — SEC Form 8-K Item 1.05 cyber disclosure obligations and elevated nation-state interest in earnings/M&A intelligence')
  }

  if (processesPayments) {
    adj.attackVectors.payment_card_compromise = (adj.attackVectors.payment_card_compromise || 0) + 0.20
    adj.threatActors.financial_fraud_groups = (adj.threatActors.financial_fraud_groups || 0) + 0.15
    adj.targetAssets.payment_data = (adj.targetAssets.payment_data || 0) + 0.20
    evidence.push('Processes payment cards — PCI DSS scope, FIN7-style POS targeting risk')
  }

  if (handlesHealthData) {
    adj.threatActors.ransomware_gangs = (adj.threatActors.ransomware_gangs || 0) + 0.15
    adj.targetAssets.patient_data = (adj.targetAssets.patient_data || 0) + 0.20
    evidence.push('Handles protected health information — HIPAA breach reporting + ransomware operator focus on healthcare')
  }

  if (isGovContractor) {
    adj.threatActors.nation_state = (adj.threatActors.nation_state || 0) + 0.15
    adj.attackVectors.supply_chain = (adj.attackVectors.supply_chain || 0) + 0.10
    adj.targetAssets.controlled_unclassified_information = (adj.targetAssets.controlled_unclassified_information || 0) + 0.15
    evidence.push('Government contractor — CMMC scope and elevated nation-state APT interest in CUI')
  }

  if (operatesInternationally) {
    adj.threatActors.nation_state = (adj.threatActors.nation_state || 0) + 0.05
    evidence.push('Operates internationally — broader threat actor pool, GDPR exposure, geopolitical risk')
  }

  return { adjustments: adj, evidence }
}

function asBoolean(response) {
  if (!response) return false
  const v = typeof response === 'object' ? response.value : response
  if (typeof v === 'string') return v.toLowerCase() === 'yes' || v === 'true'
  return !!v
}
